import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { getPolicySummary } from "./rbac";

const BRAND = {
  red: "#D73C2E",
  black: "#252935",
  mix: "#B9343A",
  redSoft: "#fff2ef",
  redSoftAlt: "#fff7f5",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  borderSoft: "rgba(185,52,58,0.12)",
  shadow: "rgba(37,41,53,0.08)",
  shadowStrong: "rgba(185,52,58,0.18)",
  text: "#252935",
  muted: "#6b6f7d",
  mutedSoft: "#8b90a0",
};

// ── Action metadata — single source of truth ───────────────────
const ACTION_META = {
  CREATE_PROFILE: {
    label: "Profile Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_PROFILE: {
    label: "Profile Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  CREATE_FUTURE_PROSPECT: {
    label: "Future Prospect Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_FUTURE_PROSPECT: {
    label: "Future Prospect Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  UPDATE_FUTURE_PROSPECT_STATUS: {
    label: "Future Prospect Status Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  ADD_REMARK: {
    label: "Note Added",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  ADD_NOTE: {
    label: "Note Added",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_NOTE: {
    label: "Note Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  FOLLOW_UP_UPDATED: {
    label: "Follow-up Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  FOLLOW_UP_RESCHEDULED: {
    label: "Follow-up Rescheduled",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  FOLLOW_UP_COMPLETED: {
    label: "Follow-up Completed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  FOLLOW_UP_CANCELLED: {
    label: "Follow-up Cancelled",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  CREATE_RECOMMENDATION: {
    label: "Recommendation Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_RECOMMENDATION_STATUS: {
    label: "Recommendation Status Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  ADD_MATCH_OUTCOME: {
    label: "Match Outcome Added",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  CALL_MADE: {
    label: "Call Logged",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  MEETING_DONE: {
    label: "Meeting Completed",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  MEETING_CONVERTED: {
    label: "Meeting Converted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DUPLICATE_DETECTED: {
    label: "Duplicate Detected",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  PROFILE_DUPLICATE_CASE_CREATED: {
    label: "Duplicate Case Created",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  DUPLICATE_ACTION_COMPLETED: {
    label: "Duplicate Review Completed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DUPLICATE_CASE_RETAINED: {
    label: "Duplicate Retained",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DUPLICATE_CASE_MERGED: {
    label: "Duplicate Merged",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DUPLICATE_CASE_REPLACED: {
    label: "Duplicate Replaced",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DUPLICATE_MERGE_HISTORY_CREATED: {
    label: "Merge History Created",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  DUPLICATE_REPLACE_HISTORY_CREATED: {
    label: "Replace History Created",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  UPLOAD_IMAGES: {
    label: "Photos Added",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPLOAD_DOCUMENTS: {
    label: "Documents Added",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DELETE_IMAGE: {
    label: "Photo Deleted",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DELETE_DOCUMENT: {
    label: "Document Deleted",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_PROFILE: {
    label: "Profile Viewed",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  VIEW_ASSIGNED_PROFILES: {
    label: "Assigned Profiles Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PHONE: {
    label: "Phone Viewed",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_EMAIL: {
    label: "Email Viewed",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  COPY_PHONE: {
    label: "Phone Copied",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  COPY_EMAIL: {
    label: "Email Copied",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_PRIMARY_PHOTO: {
    label: "Primary Photo Viewed",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  VIEW_PROFILE_IMAGE: {
    label: "Profile Photo Viewed",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  OPEN_PROFILE_DOCUMENT: {
    label: "Document Opened",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DOWNLOAD_PROFILE_DOCUMENT: {
    label: "Document Downloaded",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DOWNLOAD_WATERMARKED_BIODATA_PDF: {
    label: "Watermarked PDF Downloaded",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_WATERMARKED_PDF_PREVIEW: {
    label: "Watermarked PDF Previewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_AUDIT_LOGS: {
    label: "Audit Logs Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_RECOMMENDATION_DASHBOARD: {
    label: "Recommendation Dashboard Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_FUTURE_PROSPECTS_TAB: {
    label: "Future Prospects Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_HRMS_ANALYTICS_SUMMARY: {
    label: "HRMS Analytics Summary Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_HRMS_ANALYTICS_USERS: {
    label: "HRMS Analytics Users Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_HRMS_ANALYTICS_USER_DAILY: {
    label: "HRMS Analytics User Detail Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_HRMS_ANALYTICS_USER_HISTORY: {
    label: "HRMS Analytics User History Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  REBUILD_USER_DAILY_METRICS: {
    label: "User Daily Metrics Rebuilt",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  VIEW_TASK_ASSIGNMENT: {
    label: "Task Assignment Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_VERIFICATION_QUEUE: {
    label: "Profile Verification Queue Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_ASSIGNED_PROFILE_PERFORMANCE: {
    label: "Assigned Profile Performance Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_AUDIT: {
    label: "Profile Audit Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_NOTES_META: {
    label: "Note Metadata Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_RECOMMENDATIONS_META: {
    label: "Recommendation Metadata Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_NOTES: {
    label: "Profile Notes Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_RECOMMENDATIONS: {
    label: "Profile Recommendations Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_DUPLICATE_CASES: {
    label: "Duplicate Cases Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_DUPLICATE_CASE: {
    label: "Duplicate Case Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_MERGE_HISTORY: {
    label: "Merge History Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  RUN_DUPLICATE_CHECK: {
    label: "Duplicate Check Run",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  DUPLICATE_CASE_RESOLVE_STARTED: {
    label: "Duplicate Resolve Started",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  SET_PRIMARY_IMG: {
    label: "Primary Photo Set",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  SET_PRIMARY_IMAGE: {
    label: "Primary Photo Set",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  LOGIN: {
    label: "Login",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  LOGIN_GOOGLE: {
    label: "Google Login",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  LOGOUT: {
    label: "Logout",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_AUTH_ME: {
    label: "Current User Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  CREATE_USER: {
    label: "User Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_USER_ROLE: {
    label: "Role Updated",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_USER_TYPE: {
    label: "User Type Updated",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DELETE_USER: {
    label: "User Deleted",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_USERS: {
    label: "Users Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_ROLES: {
    label: "Roles Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  PROFILE_AUTO_VERIFIED: {
    label: "Profile Auto Verified",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  PROFILE_VERIFIED_MANUALLY: {
    label: "Profile Verified",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  PROFILE_UNVERIFIED_MANUALLY: {
    label: "Profile Unverified",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  ACCESS_POLICY_CREATED: {
    label: "Policy Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  ACCESS_POLICY_UPDATED: {
    label: "Policy Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  ACCESS_POLICY_DELETED: {
    label: "Policy Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  REFERRAL_SOURCE_CREATED: {
    label: "Referral Source Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  REFERRAL_SOURCE_UPDATED: {
    label: "Referral Source Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  REFERRAL_SOURCE_DELETED: {
    label: "Referral Source Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_REFERRAL_SOURCES: {
    label: "Referral Sources Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DROPDOWN_CREATED: {
    label: "Dropdown Value Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  DROPDOWN_UPDATED: {
    label: "Dropdown Value Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  DROPDOWN_DELETED: {
    label: "Dropdown Value Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_DROPDOWN_MASTERS: {
    label: "Dropdown Masters Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_ACCESS_POLICIES: {
    label: "Access Policies Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_RBAC_SUMMARY: {
    label: "RBAC Summary Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_RBAC_DIAGNOSTICS: {
    label: "RBAC Diagnostics Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_RBAC_ROLE_ACCESS: {
    label: "RBAC Role Access Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  RBAC_ROLE_CREATED: {
    label: "RBAC Role Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  RBAC_ROLE_UPDATED: {
    label: "RBAC Role Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_ROLE_DELETED: {
    label: "RBAC Role Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  RBAC_ROLE_CLONED: {
    label: "RBAC Role Cloned",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_ROLE_ACCESS_UPDATED: {
    label: "RBAC Role Access Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_PERMISSION_CREATED: {
    label: "RBAC Permission Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  RBAC_PERMISSION_UPDATED: {
    label: "RBAC Permission Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_PERMISSION_DELETED: {
    label: "RBAC Permission Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  RBAC_FEATURE_CREATED: {
    label: "RBAC Feature Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  RBAC_FEATURE_UPDATED: {
    label: "RBAC Feature Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_FEATURE_DELETED: {
    label: "RBAC Feature Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  RBAC_FEATURE_PERMISSIONS_UPDATED: {
    label: "RBAC Feature Permissions Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  RBAC_USER_TYPE_CREATED: {
    label: "User Type Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  RBAC_USER_TYPE_UPDATED: {
    label: "User Type Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  CITY_TIER_CITIES_UPDATED: {
    label: "City Tier Cities Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  ASSIGN_PROFILE_TASKS: {
    label: "Profiles Assigned",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  UNASSIGN_PROFILE_TASK: {
    label: "Profile Unassigned",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_PROFILE_ASSIGNMENTS: {
    label: "Profile Assignments Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  CREATE_TASK: {
    label: "Task Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  UPDATE_TASK: {
    label: "Task Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  CHANGE_TASK_STATUS: {
    label: "Task Status Updated",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  REASSIGN_TASK: {
    label: "Task Reassigned",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_TASK_USERS: {
    label: "Task Users Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_TASK_DASHBOARD: {
    label: "Task Dashboard Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_MY_TASKS: {
    label: "My Tasks Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_ASSIGNED_BY_ME_TASKS: {
    label: "Assigned By Me Tasks Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_MANAGED_TASKS: {
    label: "Managed Tasks Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_TASK_HISTORY: {
    label: "Task History Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_BIODATA_ASSIGNMENT_DASHBOARD: {
    label: "Bio Data Dashboard Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_MY_BIODATA_ASSIGNMENTS: {
    label: "My Bio Data Assignments Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  VIEW_ASSIGNED_BY_ME_BIODATA_ASSIGNMENTS: {
    label: "Bio Data Assigned By Me Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  CREATE_BIODATA_ASSIGNMENT: {
    label: "Bio Data Assignment Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  OPEN_BIODATA_ASSIGNMENT_WORKSPACE: {
    label: "Bio Data Workspace Opened",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  OPEN_BIODATA_ASSIGNMENT_FILE: {
    label: "Bio Data File Opened",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  UPDATE_BIODATA_ASSIGNMENT_STATUS: {
    label: "Bio Data Status Updated",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  COMPLETE_BIODATA_ASSIGNMENT: {
    label: "Bio Data Assignment Completed",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_IMPORT_EXPORT_WORKSPACE: {
    label: "Import Export Workspace Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  IMPORT_PROFILE_CSV: {
    label: "CSV Import Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  VIEW_IMPORT_BATCH: {
    label: "Import Batch Viewed",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  DELETE_IMPORT_BATCH: {
    label: "Import Batch Deleted",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  FINALIZE_IMPORT_BATCH: {
    label: "Import Batch Finalized",
    color: BRAND.mix,
    bg: BRAND.redSoft,
  },
  SEARCH_EXPORT_PROFILES: {
    label: "Export Profile Search",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
  CREATE_BULK_EXPORT: {
    label: "Bulk Export Created",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  ADD_PAYMENT: {
    label: "Payment Added",
    color: BRAND.red,
    bg: BRAND.redSoft,
  },
  ACCESS_DENIED: {
    label: "Access Denied",
    color: BRAND.black,
    bg: BRAND.redSoftAlt,
  },
};

const EMPTY_LOOKUPS = {
  genders: [],
  maritalStatuses: [],
  complexions: [],
  bloodGroups: [],
  locations: [],
  tags: [],
};

const CATEGORY_DEFINITIONS = [
  { key: "profiles", label: "Profiles" },
  { key: "notes", label: "Notes" },
  { key: "recommendations", label: "Recommendations" },
  { key: "access", label: "Profile Access" },
  { key: "media", label: "Media" },
  { key: "payments", label: "Payments" },
  { key: "duplicates", label: "Duplicates And Merge" },
  { key: "imports_exports", label: "Import And Export" },
  { key: "admin", label: "Admin And RBAC" },
  { key: "reports", label: "Audit And Reports" },
  { key: "auth", label: "Authentication" },
  { key: "users", label: "Users" },
  { key: "other", label: "Other" },
];

const COMPLEX_FIELDS = new Set([
  "educations",
  "education",
  "contacts",
  "relatives",
  "family_members",
  "preferences",
  "tags",
  "child_tables_updated",
]);

const AUDIT_DEFAULT_PAGE_SIZE = 100;
const AUDIT_PAGE_SIZE_OPTIONS = [50, 100, 250, 500];
const AUDIT_ACTOR_OPTION_LIMIT = 5000;

export default function AuditLogs({ token, user = null }) {
  const [logs, setLogs] = useState([]);
  const [actorOptions, setActorOptions] = useState([]);
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading activity timeline...");
  const [error, setError] = useState("");
  const [hasLoadedWorkspace, setHasLoadedWorkspace] = useState(false);
  const [auditMeta, setAuditMeta] = useState({
    total: 0,
    offset: 0,
    limit: AUDIT_DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });

  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(AUDIT_DEFAULT_PAGE_SIZE);
  const auditPolicy = useMemo(() => getPolicySummary(user, "audit", "view"), [user]);
  const hasAuditPolicyNotice = useMemo(() => (
    Number(auditPolicy?.max_results) > 0 || auditPolicy?.denied
  ), [auditPolicy]);
  const knownActionKeys = useMemo(() => (
    Object.keys(ACTION_META).sort((left, right) => (
      formatActionOptionLabel(left).localeCompare(formatActionOptionLabel(right))
    ))
  ), []);

  const fetchAuditPage = async (page = currentPage, size = pageSize) => {
    const offset = Math.max((page - 1) * size, 0);
    const params = {
      include_meta: 1,
      limit: size,
      offset,
    };

    if (filterUser !== "all") {
      params.actor_user_id = filterUser;
    }

    if (filterAction !== "all") {
      params.action = filterAction;
    } else if (filterCategory !== "all") {
      if (filterCategory === "other") {
        params.exclude_actions = knownActionKeys.join(",");
      } else {
        const categoryActions = knownActionKeys.filter(
          (action) => getActionCategoryKey(action) === filterCategory
        );
        if (categoryActions.length) {
          params.actions = categoryActions.join(",");
        }
      }
    }

    const logsRes = await API.get("/audit-logs", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });

    const payload = logsRes.data || {};
    const items = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload)
        ? payload
        : [];
    const total = Number(payload?.total || offset + items.length || 0);
    const limit = Math.max(Number(payload?.limit || size || AUDIT_DEFAULT_PAGE_SIZE), 1);
    const totalPages = total > 0 ? Math.ceil(total / limit) : 1;

    return { items, total, offset, limit, totalPages };
  };

  const fetchReferenceData = async () => {
    const [
      gRes,
      msRes,
      cxRes,
      bgRes,
      locRes,
      tagRes,
      actorsRes,
    ] = await Promise.all([
      API.get("/genders"),
      API.get("/marital-status"),
      API.get("/complexions"),
      API.get("/blood-groups"),
      API.get("/locations"),
      API.get("/tags"),
      API.get("/audit-actors", {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: AUDIT_ACTOR_OPTION_LIMIT },
      }),
    ]);

    setLookups({
      genders: Array.isArray(gRes.data) ? gRes.data : [],
      maritalStatuses: Array.isArray(msRes.data) ? msRes.data : [],
      complexions: Array.isArray(cxRes.data) ? cxRes.data : [],
      bloodGroups: Array.isArray(bgRes.data) ? bgRes.data : [],
      locations: Array.isArray(locRes.data) ? locRes.data : [],
      tags: Array.isArray(tagRes.data) ? tagRes.data : [],
    });
    setActorOptions(
      Array.isArray(actorsRes.data?.items)
        ? actorsRes.data.items.filter((item) => item?.id)
        : []
    );
  };

  useEffect(() => {
    let cancelled = false;

    const loadInitialWorkspace = async () => {
      try {
        setLoading(true);
        setLoadingPage(false);
        setLoadingText("Loading activity timeline...");
        setError("");
        setLogs([]);
        setAuditMeta({
          total: 0,
          offset: 0,
          limit: pageSize,
          totalPages: 1,
        });

        const [pageResult] = await Promise.all([
          fetchAuditPage(1, pageSize),
          fetchReferenceData(),
        ]);

        if (cancelled) return;

        setLogs(pageResult.items);
        setAuditMeta({
          total: pageResult.total,
          offset: pageResult.offset,
          limit: pageResult.limit,
          totalPages: pageResult.totalPages,
        });
        setLoadingText(
          pageResult.total > 0
            ? `Loading activity timeline... ${Math.min(pageResult.items.length, pageResult.total)}/${pageResult.total}`
            : "Loading activity timeline..."
        );
        setHasLoadedWorkspace(true);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load logs", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load activity log. Please try again."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitialWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasLoadedWorkspace) return;

    let cancelled = false;

    const loadPagedLogs = async () => {
      try {
        setLoadingPage(true);
        setError("");
        const pageResult = await fetchAuditPage(currentPage, pageSize);
        if (cancelled) return;

        if (currentPage > pageResult.totalPages) {
          setCurrentPage(pageResult.totalPages);
          return;
        }

        setLogs(pageResult.items);
        setAuditMeta({
          total: pageResult.total,
          offset: pageResult.offset,
          limit: pageResult.limit,
          totalPages: pageResult.totalPages,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load audit page", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load activity log. Please try again."
        );
      } finally {
        if (!cancelled) {
          setLoadingPage(false);
        }
      }
    };

    loadPagedLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedWorkspace, currentPage, pageSize, filterUser, filterAction, filterCategory]);

  const actionKeys = useMemo(() => {
    const live = [...new Set(logs.map((l) => l.action).filter(Boolean))].sort((left, right) => (
      formatActionOptionLabel(left).localeCompare(formatActionOptionLabel(right))
    ));
    const known = new Set(knownActionKeys);

    return [
      ...knownActionKeys,
      ...live.filter((k) => !known.has(k)),
    ];
  }, [knownActionKeys, logs]);

  const categoryOptions = useMemo(() => {
    return CATEGORY_DEFINITIONS;
  }, []);

  const availableActionKeys = useMemo(() => {
    if (filterCategory === "all") return actionKeys;
    return actionKeys.filter((action) => getActionCategoryKey(action) === filterCategory);
  }, [actionKeys, filterCategory]);

  useEffect(() => {
    if (filterAction !== "all" && !availableActionKeys.includes(filterAction)) {
      setFilterAction("all");
    }
  }, [availableActionKeys, filterAction]);

  const visibleLogs = useMemo(() => logs.filter(Boolean), [logs]);

  const stats = useMemo(() => {
    const total = auditMeta.total || logs.length;
    const pageEntries = logs.length;
    const updates = logs.filter((l) => l.action === "UPDATE_PROFILE").length;
    const creates = logs.filter((l) => l.action === "CREATE_PROFILE").length;
    const views = logs.filter((l) => l.action === "VIEW_PROFILE").length;

    return { total, pageEntries, updates, creates, views };
  }, [auditMeta.total, logs]);

  const pageStart = useMemo(() => (
    stats.total > 0 ? auditMeta.offset + 1 : 0
  ), [auditMeta.offset, stats.total]);

  const pageEnd = useMemo(() => (
    stats.total > 0 ? auditMeta.offset + logs.length : 0
  ), [auditMeta.offset, logs.length, stats.total]);

  const currentPageSafe = useMemo(() => (
    Math.min(Math.max(currentPage, 1), Math.max(auditMeta.totalPages || 1, 1))
  ), [auditMeta.totalPages, currentPage]);

  const pageNumbers = useMemo(() => (
    buildPageNumberList(currentPageSafe, auditMeta.totalPages || 1)
  ), [auditMeta.totalPages, currentPageSafe]);

  const refreshWorkspace = async () => {
    const showScreenLoader = !hasLoadedWorkspace;

    try {
      if (showScreenLoader) {
        setLoading(true);
        setLoadingText("Loading activity timeline...");
      } else {
        setLoadingPage(true);
      }

      setError("");
      const [pageResult] = await Promise.all([
        fetchAuditPage(currentPageSafe, pageSize),
        fetchReferenceData(),
      ]);

      if (currentPageSafe > pageResult.totalPages) {
        setCurrentPage(pageResult.totalPages);
        return;
      }

      setLogs(pageResult.items);
      setAuditMeta({
        total: pageResult.total,
        offset: pageResult.offset,
        limit: pageResult.limit,
        totalPages: pageResult.totalPages,
      });
      setHasLoadedWorkspace(true);
    } catch (err) {
      console.error("Failed to refresh logs", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load activity log. Please try again."
      );
    } finally {
      setLoading(false);
      setLoadingPage(false);
    }
  };

  if (loading) {
    return (
      <div style={S.center}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100% { opacity:.55; } 50% { opacity:1; } }
        `}</style>
        <div style={S.spinner} />
        <p style={S.loadingText}>{loadingText}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.center}>
        <div style={S.errorIcon}>!</div>
        <h3 style={S.errorTitle}>Could not load audit logs</h3>
        <p style={S.errorText}>{error}</p>
        <button style={S.primaryBtn} onClick={refreshWorkspace}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={S.wrap}>
        <div style={S.hero}>
          <div>
            <div style={S.eyebrow}>System intelligence</div>
            <h1 style={S.pageTitle}>Activity Timeline</h1>
            <p style={S.pageSub}>
              Track profile edits, phone reveals, logins, notes, uploads and
              admin actions across Jain Kalyanam CRM.
            </p>
          </div>

          <button
            style={{ ...S.primaryBtn, ...(loadingPage ? S.primaryBtnDisabled : {}) }}
            onClick={refreshWorkspace}
            disabled={loadingPage}
          >
            {loadingPage ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {hasAuditPolicyNotice && (
          <div style={S.policyNotice}>
            <strong style={S.policyNoticeTitle}>Audit access policy is active.</strong>
            <div style={S.policyNoticeBody}>
              {Number(auditPolicy?.max_results) > 0 && (
                <span>Audit timeline is limited to {auditPolicy.max_results} accessible rows by policy.</span>
              )}
              {auditPolicy?.denied && (
                <span>Some audit visibility is restricted by policy.</span>
              )}
            </div>
          </div>
        )}

        <div style={S.statGrid}>
          <StatCard label="Total Entries" value={stats.total} />
          <StatCard label="Page Entries" value={stats.pageEntries} />
          <StatCard label="Updates On Page" value={stats.updates} />
          <StatCard label="Creates On Page" value={stats.creates} />
          <StatCard label="Views On Page" value={stats.views} />
        </div>

        <div style={S.filtersCard}>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>User</label>
            <select
              style={{ ...S.select, minWidth: 280, width: 280 }}
              value={filterUser}
              onChange={(e) => {
                setFilterUser(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Users</option>
              {actorOptions.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.label || u.display_name || u.email || `User #${u.id}`}
                </option>
              ))}
            </select>
          </div>

          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Category</label>
            <select
              style={S.select}
              value={filterCategory}
              onChange={(e) => {
                const nextCategory = e.target.value;
                setFilterCategory(nextCategory);
                setCurrentPage(1);
                if (
                  filterAction !== "all" &&
                  nextCategory !== "all" &&
                  getActionCategoryKey(filterAction) !== nextCategory
                ) {
                  setFilterAction("all");
                }
              }}
            >
              <option value="all">All Categories</option>
              {categoryOptions.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Action</label>
            <select
              style={S.select}
              value={filterAction}
              onChange={(e) => {
                const nextAction = e.target.value;
                setFilterAction(nextAction);
                setCurrentPage(1);
                if (nextAction === "all") return;
                const nextCategory = getActionCategoryKey(nextAction);
                if (nextCategory && nextCategory !== "other" && filterCategory !== nextCategory) {
                  setFilterCategory(nextCategory);
                }
              }}
            >
              <option value="all">All Actions</option>
              {availableActionKeys.map((a) => (
                <option key={a} value={a}>
                  {formatActionOptionLabel(a)}
                </option>
              ))}
            </select>
          </div>

          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Page Size</label>
            <select
              style={S.select}
              value={String(pageSize)}
              onChange={(e) => {
                setPageSize(Number(e.target.value) || AUDIT_DEFAULT_PAGE_SIZE);
                setCurrentPage(1);
              }}
            >
              {AUDIT_PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={String(option)}>
                  {option} per page
                </option>
              ))}
            </select>
          </div>

          {(filterUser !== "all" || filterCategory !== "all" || filterAction !== "all") && (
            <button
              style={S.clearBtn}
              onClick={() => {
                setFilterUser("all");
                setFilterCategory("all");
                setFilterAction("all");
                setCurrentPage(1);
              }}
            >
              Clear
            </button>
          )}

          <span style={S.countPill}>
            {stats.total} matching entries
          </span>
        </div>

        {(stats.total > 0 || loadingPage) && (
          <div style={S.loadSummary}>
            {loadingPage
              ? "Updating activity timeline..."
              : `Showing ${pageStart}-${pageEnd} of ${stats.total} audit entries | Page ${currentPageSafe} of ${auditMeta.totalPages}`}
          </div>
        )}

        <div style={S.list}>
          {visibleLogs.length === 0 ? (
            <div style={S.emptyState}>
              <div style={S.emptyIcon}>◎</div>
              <h3 style={S.emptyTitle}>No activity found</h3>
              <p style={S.emptyText}>
                Try changing filters or refreshing the audit timeline.
              </p>
            </div>
          ) : (
            visibleLogs.map((log) => (
              <LogItem key={log.id} log={log} lookups={lookups} />
            ))
          )}
        </div>

        {stats.total > 0 && (
          <div style={S.paginationBar}>
            <div style={S.paginationSummary}>
              Page {currentPageSafe} of {auditMeta.totalPages} | {pageSize} rows per page
            </div>
            <div style={S.paginationActions}>
              <button
                type="button"
                style={{
                  ...S.paginationBtn,
                  ...(currentPageSafe === 1 || loadingPage ? S.paginationBtnDisabled : {}),
                }}
                disabled={currentPageSafe === 1 || loadingPage}
                onClick={() => setCurrentPage(1)}
              >
                First
              </button>
              <button
                type="button"
                style={{
                  ...S.paginationBtn,
                  ...(currentPageSafe === 1 || loadingPage ? S.paginationBtnDisabled : {}),
                }}
                disabled={currentPageSafe === 1 || loadingPage}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>

              {pageNumbers.map((pageNumber, index) => (
                pageNumber === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} style={S.paginationEllipsis}>...</span>
                ) : (
                  <button
                    key={pageNumber}
                    type="button"
                    style={{
                      ...S.paginationBtn,
                      ...(pageNumber === currentPageSafe ? S.paginationBtnActive : {}),
                      ...(loadingPage ? S.paginationBtnDisabled : {}),
                    }}
                    disabled={loadingPage}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                )
              ))}

              <button
                type="button"
                style={{
                  ...S.paginationBtn,
                  ...(currentPageSafe >= auditMeta.totalPages || loadingPage ? S.paginationBtnDisabled : {}),
                }}
                disabled={currentPageSafe >= auditMeta.totalPages || loadingPage}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, auditMeta.totalPages))}
              >
                Next
              </button>
              <button
                type="button"
                style={{
                  ...S.paginationBtn,
                  ...(currentPageSafe >= auditMeta.totalPages || loadingPage ? S.paginationBtnDisabled : {}),
                }}
                disabled={currentPageSafe >= auditMeta.totalPages || loadingPage}
                onClick={() => setCurrentPage(auditMeta.totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function buildPageNumberList(currentPage, totalPages) {
  const safeTotal = Math.max(Number(totalPages) || 1, 1);
  if (safeTotal <= 7) {
    return Array.from({ length: safeTotal }, (_, index) => index + 1);
  }

  const pages = new Set([1, safeTotal, currentPage - 1, currentPage, currentPage + 1]);
  const ordered = [...pages]
    .filter((page) => page >= 1 && page <= safeTotal)
    .sort((left, right) => left - right);
  const display = [];

  ordered.forEach((page, index) => {
    if (index > 0 && page - ordered[index - 1] > 1) {
      display.push("ellipsis");
    }
    display.push(page);
  });

  return display;
}

function StatCard({ label, value }) {
  return (
    <div style={S.statCard}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function LogItem({ log, lookups }) {
  const actionMeta = getActionMeta(log.action);
  const diff = parseDiff(log, lookups);
  const note = parseNote(log);
  const actor = getActorName(log);
  const targetSummary = getAuditEntitySummary(log);

  return (
    <div style={S.item}>
      <div style={{ ...S.timelineDot, background: actionMeta.color }} />

      <div style={S.itemHeader}>
        <div style={S.itemLeft}>
          <span
            style={{
              ...S.actionBadge,
              background: actionMeta.bg,
              color: actionMeta.color,
            }}
          >
            {actionMeta.label}
          </span>

          <div style={S.itemText}>
            <b style={S.actor}>{actor}</b>
            <span style={S.verb}>{formatAction(log, targetSummary)}</span>
          </div>
        </div>

        <div style={S.itemRight}>
          <span style={S.time}>{formatTime(log.created_at)}</span>
          {log.entity_type && (
            <span style={S.entityBadge}>{formatEntityTypeLabel(log.entity_type)}</span>
          )}
        </div>
      </div>

      {(log.ip_address || log.user_agent || log.request_id) && (
        <div style={S.metaLine}>
          {log.ip_address && <span>IP: {formatIpAddress(log.ip_address)}</span>}
          {log.request_id && <span>Req: {shorten(log.request_id, 16)}</span>}
        </div>
      )}

      {note && (
        <div style={S.noteBox}>
          <span style={S.noteIcon}>✎</span>
          <span style={S.noteText}>{note}</span>
        </div>
      )}

      {diff && diff.length > 0 && (
        <div style={S.diffBox}>
          <div style={S.diffHeading}>Changes</div>
          <div style={S.diffTable}>
            {diff.map(({ field, oldVal, newVal, isComplex }) =>
              isComplex ? (
                <ComplexDiffRow
                  key={field}
                  field={field}
                  oldVal={oldVal}
                  newVal={newVal}
                />
              ) : (
                <SimpleDiffRow
                  key={field}
                  field={field}
                  oldVal={oldVal}
                  newVal={newVal}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SimpleDiffRow({ field, oldVal, newVal }) {
  return (
    <div style={S.diffRow}>
      <span style={S.diffField}>{formatFieldLabel(field)}</span>
      <span style={S.diffOld}>{formatScalar(oldVal)}</span>
      <span style={S.diffArrow}>→</span>
      <span style={S.diffNew}>{formatScalar(newVal)}</span>
    </div>
  );
}

function ComplexDiffRow({ field, oldVal, newVal }) {
  const [expanded, setExpanded] = useState(false);

  const oldValue = safeParseFlexible(oldVal);
  const newValue = safeParseFlexible(newVal);

  return (
    <div style={S.complexWrap}>
      <div style={S.diffRow}>
        <span style={S.diffField}>{formatFieldLabel(field)}</span>
        <span style={S.listChanged}>Structured data changed</span>
        <button style={S.expandBtn} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Hide" : "Show"}
        </button>
      </div>

      {expanded && (
        <div style={S.complexGrid}>
          <div style={S.complexPanel}>
            <span style={S.complexLabelMuted}>Before</span>
            <pre style={S.pre}>{prettyJson(oldValue)}</pre>
          </div>

          <div style={{ ...S.complexPanel, borderColor: BRAND.red }}>
            <span style={S.complexLabelGreen}>After</span>
            <pre style={S.pre}>{prettyJson(newValue)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit parsing helpers ──────────────────────────────────────

function getActionMeta(action) {
  return (
    ACTION_META[action] || {
      label: formatActionOptionLabel(action),
      color: BRAND.red,
      bg: BRAND.redSoft,
    }
  );
}

function formatActionOptionLabel(action) {
  const raw = String(action || "Activity").trim();
  if (!raw) return "Activity";

  const acronyms = new Set(["RBAC", "PDF", "API", "CRM", "JK"]);

  return raw
    .toLowerCase()
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      const upper = part.toUpperCase();
      if (acronyms.has(upper)) return upper;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

function getActionCategoryKey(action) {
  const value = String(action || "").trim().toUpperCase();
  if (!value) return "other";

  if ([
    "CREATE_PROFILE",
    "UPDATE_PROFILE",
    "CREATE_FUTURE_PROSPECT",
    "UPDATE_FUTURE_PROSPECT",
    "UPDATE_FUTURE_PROSPECT_STATUS",
    "PROFILE_AUTO_VERIFIED",
    "PROFILE_VERIFIED_MANUALLY",
    "PROFILE_UNVERIFIED_MANUALLY",
  ].includes(value)) return "profiles";

  if (
    [
      "ADD_REMARK",
      "ADD_NOTE",
      "UPDATE_NOTE",
      "FOLLOW_UP_UPDATED",
      "FOLLOW_UP_RESCHEDULED",
      "FOLLOW_UP_COMPLETED",
      "FOLLOW_UP_CANCELLED",
      "VIEW_PROFILE_NOTES_META",
      "VIEW_PROFILE_NOTES",
    ].includes(value)
  ) {
    return "notes";
  }

  if (
    [
      "CREATE_RECOMMENDATION",
      "UPDATE_RECOMMENDATION_STATUS",
      "ADD_MATCH_OUTCOME",
      "CALL_MADE",
      "MEETING_DONE",
      "MEETING_CONVERTED",
      "VIEW_PROFILE_RECOMMENDATIONS_META",
      "VIEW_PROFILE_RECOMMENDATIONS",
    ].includes(value)
  ) {
    return "recommendations";
  }

  if (
    [
      "VIEW_PROFILE",
      "VIEW_PHONE",
      "VIEW_EMAIL",
      "COPY_PHONE",
      "COPY_EMAIL",
      "OPEN_PROFILE_DOCUMENT",
      "DOWNLOAD_PROFILE_DOCUMENT",
      "DOWNLOAD_BIODATA_PDF",
      "DOWNLOAD_WATERMARKED_BIODATA_PDF",
      "VIEW_PRIMARY_PHOTO",
      "VIEW_PROFILE_IMAGE",
      "VIEW_WATERMARKED_PDF_PREVIEW",
      "VIEW_ASSIGNED_PROFILES",
      "VIEW_FUTURE_PROSPECTS_TAB",
    ].includes(value)
  ) {
    return "access";
  }

  if (
    [
      "UPLOAD_IMAGES",
      "UPLOAD_DOCUMENTS",
      "DELETE_IMAGE",
      "DELETE_DOCUMENT",
      "SET_PRIMARY_IMG",
      "SET_PRIMARY_IMAGE",
    ].includes(value)
  ) {
    return "media";
  }

  if (["ADD_PAYMENT"].includes(value)) return "payments";

  if (
    value.startsWith("DUPLICATE_") ||
    value.startsWith("PROFILE_DUPLICATE_") ||
    value === "VIEW_MERGE_HISTORY" ||
    value === "RUN_DUPLICATE_CHECK" ||
    value === "VIEW_DUPLICATE_CASE" ||
    value === "VIEW_DUPLICATE_CASES"
  ) {
    return "duplicates";
  }

  if (
    [
      "VIEW_IMPORT_EXPORT_WORKSPACE",
      "IMPORT_PROFILE_CSV",
      "VIEW_IMPORT_BATCH",
      "DELETE_IMPORT_BATCH",
      "FINALIZE_IMPORT_BATCH",
      "SEARCH_EXPORT_PROFILES",
      "CREATE_BULK_EXPORT",
    ].includes(value)
  ) {
    return "imports_exports";
  }

  if (
    value.startsWith("RBAC_") ||
    value.startsWith("DROPDOWN_") ||
    value.startsWith("ACCESS_POLICY_") ||
    value.startsWith("REFERRAL_SOURCE_") ||
    value.startsWith("VIEW_RBAC_") ||
    value === "VIEW_DROPDOWN_MASTERS" ||
    value === "VIEW_ACCESS_POLICIES" ||
    value === "VIEW_REFERRAL_SOURCES" ||
    value === "CITY_TIER_CITIES_UPDATED"
  ) {
    return "admin";
  }

  if (
    [
      "VIEW_AUDIT_LOGS",
      "VIEW_PROFILE_AUDIT",
      "VIEW_PROFILE_VERIFICATION_QUEUE",
      "VIEW_RECOMMENDATION_DASHBOARD",
      "VIEW_HRMS_ANALYTICS_SUMMARY",
      "VIEW_HRMS_ANALYTICS_USERS",
      "VIEW_HRMS_ANALYTICS_USER_DAILY",
      "VIEW_HRMS_ANALYTICS_USER_HISTORY",
      "REBUILD_USER_DAILY_METRICS",
      "VIEW_TASK_ASSIGNMENT",
      "VIEW_ASSIGNED_PROFILE_PERFORMANCE",
      "VIEW_TASK_USERS",
      "VIEW_TASK_DASHBOARD",
      "VIEW_MY_TASKS",
      "VIEW_ASSIGNED_BY_ME_TASKS",
      "VIEW_MANAGED_TASKS",
      "VIEW_TASK_HISTORY",
      "VIEW_BIODATA_ASSIGNMENT_DASHBOARD",
      "VIEW_MY_BIODATA_ASSIGNMENTS",
      "VIEW_ASSIGNED_BY_ME_BIODATA_ASSIGNMENTS",
      "CREATE_BIODATA_ASSIGNMENT",
      "OPEN_BIODATA_ASSIGNMENT_WORKSPACE",
      "OPEN_BIODATA_ASSIGNMENT_FILE",
      "UPDATE_BIODATA_ASSIGNMENT_STATUS",
      "COMPLETE_BIODATA_ASSIGNMENT",
      "CREATE_TASK",
      "UPDATE_TASK",
      "CHANGE_TASK_STATUS",
      "REASSIGN_TASK",
    ].includes(value)
  ) {
    return "reports";
  }

  if (["LOGIN", "LOGIN_GOOGLE", "LOGOUT", "VIEW_AUTH_ME"].includes(value)) return "auth";

  if (
    [
      "CREATE_USER",
      "UPDATE_USER_ROLE",
      "UPDATE_USER_TYPE",
      "DELETE_USER",
      "VIEW_USERS",
      "VIEW_ROLES",
      "ASSIGN_PROFILE_TASKS",
      "UNASSIGN_PROFILE_TASK",
      "VIEW_PROFILE_ASSIGNMENTS",
    ].includes(value)
  ) {
    return "users";
  }

  return "other";
}

function getActorId(log) {
  return log.actor_user_id || log.user_id || log.created_by || null;
}

function getActorName(log) {
  const actorEmail = String(log.user_email || log.actor_email || "").trim();
  const actorName = String(log.user_name || "").trim();
  return (
    formatUserAuditLabel(actorName, actorEmail) ||
    actorName ||
    actorEmail ||
    (getActorId(log) ? `User #${getActorId(log)}` : "System")
  );
}

function formatUserAuditLabel(name, email) {
  const safeName = String(name || "").trim();
  const safeEmail = String(email || "").trim();
  return safeName || safeEmail || "";
}

function cleanAuditText(value) {
  return String(value || "")
    .replace(/Ã¢â‚¬â€/g, "-")
    .replace(/â€”/g, "-")
    .replace(/Ã‚Â·/g, " | ")
    .replace(/Â·/g, " | ")
    .replace(/·/g, " | ")
    .replace(/\s+\|\s+\|\s+/g, " | ")
    .trim();
}

function parseDiff(log, lookups = EMPTY_LOOKUPS) {
  if (![
    "UPDATE_PROFILE",
    "ADD_REMARK",
    "ADD_NOTE",
    "UPDATE_NOTE",
    "UPDATE_RECOMMENDATION_STATUS",
    "FOLLOW_UP_UPDATED",
    "FOLLOW_UP_RESCHEDULED",
    "FOLLOW_UP_COMPLETED",
    "FOLLOW_UP_CANCELLED",
    "CREATE_RECOMMENDATION",
    "ADD_MATCH_OUTCOME",
    "CALL_MADE",
    "MEETING_DONE",
    "MEETING_CONVERTED",
    "DUPLICATE_CASE_RETAINED",
    "DUPLICATE_CASE_MERGED",
    "DUPLICATE_CASE_REPLACED",
    "DUPLICATE_MERGE_HISTORY_CREATED",
    "DUPLICATE_REPLACE_HISTORY_CREATED",
    "PROFILE_AUTO_VERIFIED",
    "PROFILE_VERIFIED_MANUALLY",
    "PROFILE_UNVERIFIED_MANUALLY",
    "CREATE_FUTURE_PROSPECT",
    "UPDATE_FUTURE_PROSPECT",
    "UPDATE_FUTURE_PROSPECT_STATUS",
    "REFERRAL_SOURCE_CREATED",
    "REFERRAL_SOURCE_UPDATED",
    "REFERRAL_SOURCE_DELETED",
    "ACCESS_POLICY_CREATED",
    "ACCESS_POLICY_UPDATED",
    "ACCESS_POLICY_DELETED",
    "ASSIGN_PROFILE_TASKS",
    "UNASSIGN_PROFILE_TASK",
    "VIEW_ASSIGNED_PROFILE_PERFORMANCE",
    "REBUILD_USER_DAILY_METRICS",
    "CREATE_BIODATA_ASSIGNMENT",
    "OPEN_BIODATA_ASSIGNMENT_WORKSPACE",
    "OPEN_BIODATA_ASSIGNMENT_FILE",
    "UPDATE_BIODATA_ASSIGNMENT_STATUS",
    "COMPLETE_BIODATA_ASSIGNMENT",
    "VIEW_IMPORT_EXPORT_WORKSPACE",
    "IMPORT_PROFILE_CSV",
    "VIEW_IMPORT_BATCH",
    "DELETE_IMPORT_BATCH",
    "FINALIZE_IMPORT_BATCH",
    "SEARCH_EXPORT_PROFILES",
    "CREATE_BULK_EXPORT",
    "CREATE_TASK",
    "UPDATE_TASK",
    "CHANGE_TASK_STATUS",
    "REASSIGN_TASK",
  ].includes(log.action)) {
    return null;
  }

  const oldRaw = log.old_data ?? log.old_value;
  const newRaw = log.new_data ?? log.new_value;

  const oldVals = safeParseObject(oldRaw);
  const newVals = safeParseObject(newRaw);
  const baseValues = Object.keys(newVals).length > 0 ? newVals : oldVals;
  const keys = Object.keys(baseValues).filter(
    (key) => !["note_id", "note", "content", "outcome_notes"].includes(key)
  );

  if (keys.length === 0) return null;

  return keys.map((key) => {
    const isComplex = COMPLEX_FIELDS.has(key) || typeof newVals[key] === "object";

    return {
      field: key,
      oldVal: isComplex
        ? oldVals[key] ?? null
        : resolveValue(key, oldVals[key] ?? null, lookups),
      newVal: isComplex
        ? newVals[key] ?? null
        : resolveValue(key, newVals[key] ?? null, lookups),
      isComplex,
    };
  });
}

function parseNote(log) {
  if (![
    "ADD_REMARK",
    "ADD_NOTE",
    "UPDATE_NOTE",
    "FOLLOW_UP_UPDATED",
    "FOLLOW_UP_RESCHEDULED",
    "FOLLOW_UP_COMPLETED",
    "FOLLOW_UP_CANCELLED",
    "CREATE_RECOMMENDATION",
    "ADD_MATCH_OUTCOME",
    "CALL_MADE",
    "MEETING_DONE",
    "MEETING_CONVERTED",
    "DUPLICATE_CASE_RETAINED",
    "DUPLICATE_CASE_MERGED",
    "DUPLICATE_CASE_REPLACED",
  ].includes(log.action)) return null;

  const val = safeParseObject(log.new_data ?? log.new_value);
  const note = val?.note || val?.content || val?.outcome_notes;

  return typeof note === "string" && note.trim() ? note.trim() : null;
}

function safeParseObject(value) {
  if (value == null) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  return {};
}

function safeParseFlexible(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
}

function prettyJson(value) {
  if (value == null || value === "") return "—";
  return JSON.stringify(value, null, 2);
}

function resolveValue(field, value, lookups) {
  if (value == null || value === "") return null;

  const id = parseInt(value, 10);

  const find = (arr = [], key = "name") => {
    if (!Array.isArray(arr) || Number.isNaN(id)) return String(value);
    const item = arr.find((x) => Number(x.id) === id);
    return item?.[key] || item?.display_label || item?.name || String(value);
  };

  switch (field) {
    case "gender_id":
      return find(lookups.genders);
    case "marital_status_id":
    case "pref_preferred_marital_status_id":
      return find(lookups.maritalStatuses);
    case "complexion_id":
      return find(lookups.complexions);
    case "blood_group_id":
      return find(lookups.bloodGroups);
    case "birth_place_id":
    case "birth_place_city_id":
    case "current_location_id":
    case "city_id":
    case "pref_preferred_location_id":
      return find(lookups.locations, "city");
    case "tags":
      return String(value)
        .split(",")
        .map((tagId) => {
          const tag = lookups.tags.find((x) => Number(x.id) === Number(tagId));
          return tag?.name || `#${tagId}`;
        })
        .join(", ");
    default:
      return value;
  }
}

function formatScalar(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }
  if (value == null || value === "") return "—";

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const date = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  }

  if (typeof value === "object") return cleanAuditText(JSON.stringify(value));

  return cleanAuditText(String(value));
}

function formatAction(log, targetName) {
  const payload = safeParseObject(log.new_data ?? log.new_value);
  const exportKind = String(payload.export_type || payload.format || "").trim().toUpperCase();
  const assignmentSummary = getAssignmentProfileSummary(log);
  const assignmentTarget = getAssignmentTargetSummary(log);
  const taskSummary = getTaskSummary(log);
  const taskActorRole = getTaskActorRoleSummary(log);
  const name = targetName ? ` | ${cleanAuditText(targetName)}` : "";

  switch (log.action) {
    case "CREATE_PROFILE":
      return ` added a new profile${name}`;
    case "UPDATE_PROFILE":
      return ` edited profile${name}`;
    case "CREATE_FUTURE_PROSPECT":
      return ` created a future prospect${name}`;
    case "UPDATE_FUTURE_PROSPECT":
      return ` updated a future prospect${name}`;
    case "UPDATE_FUTURE_PROSPECT_STATUS":
      return ` updated future prospect status${name}`;
    case "ADD_REMARK":
    case "ADD_NOTE":
      return ` added a note${name}`;
    case "UPDATE_NOTE":
      return ` updated a note${name}`;
    case "FOLLOW_UP_UPDATED":
      return ` updated a follow-up${name}`;
    case "FOLLOW_UP_RESCHEDULED":
      return ` rescheduled a follow-up${name}`;
    case "FOLLOW_UP_COMPLETED":
      return ` completed a follow-up${name}`;
    case "FOLLOW_UP_CANCELLED":
      return ` cancelled a follow-up${name}`;
    case "CREATE_RECOMMENDATION":
      return ` created a recommendation${name}`;
    case "UPDATE_RECOMMENDATION_STATUS":
      return ` updated recommendation status${name}`;
    case "ADD_MATCH_OUTCOME":
      return ` logged a match outcome${name}`;
    case "CALL_MADE":
      return ` logged a call${name}`;
    case "MEETING_DONE":
      return ` completed a meeting${name}`;
    case "MEETING_CONVERTED":
      return ` converted a meeting into a match${name}`;
    case "DUPLICATE_DETECTED":
      return ` detected possible duplicates${name}`;
    case "PROFILE_DUPLICATE_CASE_CREATED":
      return " created a duplicate review case";
    case "DUPLICATE_ACTION_COMPLETED":
      return " completed duplicate review";
    case "DUPLICATE_CASE_RETAINED":
      return " retained both duplicate profiles";
    case "DUPLICATE_CASE_MERGED":
      return " merged duplicate profiles";
    case "DUPLICATE_CASE_REPLACED":
      return " replaced an older duplicate profile";
    case "DUPLICATE_MERGE_HISTORY_CREATED":
      return " wrote merge history";
    case "DUPLICATE_REPLACE_HISTORY_CREATED":
      return " wrote replace history";
    case "UPLOAD_IMAGES":
      return ` uploaded photos${name}`;
    case "UPLOAD_DOCUMENTS":
      return ` uploaded documents${name}`;
    case "DELETE_IMAGE":
      return ` deleted a photo${name}`;
    case "DELETE_DOCUMENT":
      return ` deleted a document${name}`;
    case "VIEW_PROFILE":
      return ` viewed profile${name}`;
    case "VIEW_ASSIGNED_PROFILES":
      return " viewed assigned profiles";
    case "VIEW_PHONE":
      return ` viewed phone number${name}`;
    case "VIEW_EMAIL":
      return ` viewed email${name}`;
    case "COPY_PHONE":
      return ` copied phone number${name}`;
    case "COPY_EMAIL":
      return ` copied email${name}`;
    case "OPEN_PROFILE_DOCUMENT":
      return ` opened a document${name}`;
    case "DOWNLOAD_PROFILE_DOCUMENT":
      return ` downloaded a document${name}`;
    case "DOWNLOAD_BIODATA_PDF":
      return ` downloaded biodata PDF${name}`;
    case "DOWNLOAD_WATERMARKED_BIODATA_PDF":
      return ` downloaded a watermarked PDF${name}`;
    case "VIEW_WATERMARKED_PDF_PREVIEW":
      return ` previewed a watermarked PDF${name}`;
    case "VIEW_PRIMARY_PHOTO":
      return ` viewed primary photo${name}`;
    case "VIEW_PROFILE_IMAGE":
      return ` viewed a profile photo${name}`;
    case "VIEW_AUDIT_LOGS":
      return " viewed audit logs";
    case "VIEW_RECOMMENDATION_DASHBOARD":
      return " viewed recommendation dashboard";
    case "VIEW_FUTURE_PROSPECTS_TAB":
      return " viewed future prospects";
    case "VIEW_HRMS_ANALYTICS_SUMMARY":
      return " viewed HRMS analytics summary";
    case "VIEW_HRMS_ANALYTICS_USERS":
      return " viewed HRMS analytics team metrics";
    case "VIEW_HRMS_ANALYTICS_USER_DAILY":
      return " viewed HRMS analytics user detail";
    case "VIEW_HRMS_ANALYTICS_USER_HISTORY":
      return " viewed HRMS analytics user history";
    case "REBUILD_USER_DAILY_METRICS":
      return " rebuilt user daily metrics";
    case "VIEW_TASK_ASSIGNMENT":
      return " viewed task assignment";
    case "VIEW_PROFILE_NOTES_META":
      return " viewed note metadata";
    case "VIEW_PROFILE_RECOMMENDATIONS_META":
      return " viewed recommendation metadata";
    case "VIEW_PROFILE_NOTES":
      return ` viewed profile notes${name}`;
    case "VIEW_PROFILE_RECOMMENDATIONS":
      return ` viewed profile recommendations${name}`;
    case "VIEW_DUPLICATE_CASES":
      return " viewed duplicate cases";
    case "VIEW_DUPLICATE_CASE":
      return " viewed a duplicate case";
    case "VIEW_MERGE_HISTORY":
      return " viewed merge history";
    case "RUN_DUPLICATE_CHECK":
      return ` ran duplicate check${name}`;
    case "DUPLICATE_CASE_RESOLVE_STARTED":
      return " started duplicate resolution";
    case "VIEW_PROFILE_VERIFICATION_QUEUE":
      return " viewed profile verification queue";
    case "VIEW_ASSIGNED_PROFILE_PERFORMANCE":
      return assignmentSummary
        ? ` viewed assigned profile performance for ${assignmentSummary}${assignmentTarget ? ` mapped to ${assignmentTarget}` : ""}`
        : assignmentTarget
          ? ` viewed assigned profile performance for ${assignmentTarget}`
          : " viewed assigned profile performance";
    case "VIEW_PROFILE_AUDIT":
      return ` viewed profile audit${name}`;
    case "SET_PRIMARY_IMG":
    case "SET_PRIMARY_IMAGE":
      return ` changed primary photo${name}`;
    case "LOGIN":
      return " logged in";
    case "LOGIN_GOOGLE":
      return " logged in with Google";
    case "LOGOUT":
      return " logged out";
    case "VIEW_AUTH_ME":
      return " viewed current user session";
    case "CREATE_USER":
      return " created a user";
    case "UPDATE_USER_ROLE":
      return " updated user roles";
    case "UPDATE_USER_TYPE":
      return " updated user type";
    case "DELETE_USER":
      return " deleted a user";
    case "VIEW_USERS":
      return " viewed users";
    case "VIEW_ROLES":
      return " viewed roles";
    case "PROFILE_AUTO_VERIFIED":
      return ` auto-verified a profile${name}`;
    case "PROFILE_VERIFIED_MANUALLY":
      return ` verified a profile${name}`;
    case "PROFILE_UNVERIFIED_MANUALLY":
      return ` marked a profile as unverified${name}`;
    case "ACCESS_POLICY_CREATED":
      return ` created an access policy${name}`;
    case "ACCESS_POLICY_UPDATED":
      return ` updated an access policy${name}`;
    case "ACCESS_POLICY_DELETED":
      return ` deleted an access policy${name}`;
    case "REFERRAL_SOURCE_CREATED":
      return ` created a referral source${name}`;
    case "REFERRAL_SOURCE_UPDATED":
      return ` updated a referral source${name}`;
    case "REFERRAL_SOURCE_DELETED":
      return ` deleted a referral source${name}`;
    case "VIEW_REFERRAL_SOURCES":
      return " viewed referral sources";
    case "DROPDOWN_CREATED":
      return " created a dropdown value";
    case "DROPDOWN_UPDATED":
      return " updated a dropdown value";
    case "DROPDOWN_DELETED":
      return " deleted a dropdown value";
    case "VIEW_DROPDOWN_MASTERS":
      return " viewed dropdown masters";
    case "VIEW_ACCESS_POLICIES":
      return " viewed access policies";
    case "VIEW_RBAC_SUMMARY":
      return " viewed RBAC summary";
    case "VIEW_RBAC_DIAGNOSTICS":
      return " viewed RBAC diagnostics";
    case "VIEW_RBAC_ROLE_ACCESS":
      return " viewed RBAC role access";
    case "RBAC_ROLE_CREATED":
      return " created an RBAC role";
    case "RBAC_ROLE_UPDATED":
      return " updated an RBAC role";
    case "RBAC_ROLE_DELETED":
      return " deleted an RBAC role";
    case "RBAC_ROLE_CLONED":
      return " cloned an RBAC role";
    case "RBAC_ROLE_ACCESS_UPDATED":
      return " updated RBAC role access";
    case "RBAC_PERMISSION_CREATED":
      return " created an RBAC permission";
    case "RBAC_PERMISSION_UPDATED":
      return " updated an RBAC permission";
    case "RBAC_PERMISSION_DELETED":
      return " deleted an RBAC permission";
    case "RBAC_FEATURE_CREATED":
      return " created an RBAC feature";
    case "RBAC_FEATURE_UPDATED":
      return " updated an RBAC feature";
    case "RBAC_FEATURE_DELETED":
      return " deleted an RBAC feature";
    case "RBAC_FEATURE_PERMISSIONS_UPDATED":
      return " updated RBAC feature permissions";
    case "RBAC_USER_TYPE_CREATED":
      return " created a user type";
    case "RBAC_USER_TYPE_UPDATED":
      return " updated a user type";
    case "CITY_TIER_CITIES_UPDATED":
      return " updated city tier cities";
    case "ASSIGN_PROFILE_TASKS":
      return assignmentSummary
        ? ` assigned profiles ${assignmentSummary}${assignmentTarget ? ` to ${assignmentTarget}` : " to a user"}`
        : assignmentTarget
          ? ` assigned profiles to ${assignmentTarget}`
          : " assigned profiles to a user";
    case "UNASSIGN_PROFILE_TASK":
      return assignmentSummary
        ? ` unassigned profile ${assignmentSummary}${assignmentTarget ? ` from ${assignmentTarget}` : " from a user"}`
        : assignmentTarget
          ? ` unassigned a profile from ${assignmentTarget}`
          : " unassigned a profile from a user";
    case "VIEW_PROFILE_ASSIGNMENTS":
      return assignmentTarget
        ? ` viewed profile assignments for ${assignmentTarget}`
        : " viewed profile assignments";
    case "CREATE_TASK":
      return taskSummary
        ? ` created task ${taskSummary}${assignmentTarget ? ` for ${assignmentTarget}` : ""}${taskActorRole ? ` as ${taskActorRole}` : ""}`
        : ` created a task${name}`;
    case "UPDATE_TASK":
      return taskSummary
        ? ` updated task ${taskSummary}${taskActorRole ? ` as ${taskActorRole}` : ""}`
        : ` updated a task${name}`;
    case "CHANGE_TASK_STATUS":
      return taskSummary
        ? ` changed task status for ${taskSummary}${taskActorRole ? ` as ${taskActorRole}` : ""}`
        : ` changed task status${name}`;
    case "REASSIGN_TASK":
      return taskSummary
        ? ` reassigned task ${taskSummary}${assignmentTarget ? ` to ${assignmentTarget}` : ""}${taskActorRole ? ` as ${taskActorRole}` : ""}`
        : ` reassigned a task${name}`;
    case "VIEW_TASK_USERS":
      return " viewed assignable task users";
    case "VIEW_TASK_DASHBOARD":
      return " viewed task dashboard";
    case "VIEW_MY_TASKS":
      return " viewed own tasks";
    case "VIEW_ASSIGNED_BY_ME_TASKS":
      return " viewed tasks assigned by me";
    case "VIEW_MANAGED_TASKS":
      return " viewed managed tasks";
    case "VIEW_TASK_HISTORY":
      return taskSummary
        ? ` viewed history for task ${taskSummary}`
        : " viewed task history";
    case "VIEW_BIODATA_ASSIGNMENT_DASHBOARD":
      return " viewed biodata assignment dashboard";
    case "VIEW_MY_BIODATA_ASSIGNMENTS":
      return " viewed biodata assignments assigned to me";
    case "VIEW_ASSIGNED_BY_ME_BIODATA_ASSIGNMENTS":
      return " viewed biodata assignments assigned by me";
    case "CREATE_BIODATA_ASSIGNMENT":
      return assignmentTarget
        ? ` created a biodata assignment for ${assignmentTarget}`
        : " created a biodata assignment";
    case "OPEN_BIODATA_ASSIGNMENT_WORKSPACE":
      return " opened a biodata entry workspace";
    case "OPEN_BIODATA_ASSIGNMENT_FILE":
      return " opened a biodata file";
    case "UPDATE_BIODATA_ASSIGNMENT_STATUS":
      return " updated biodata assignment status";
    case "COMPLETE_BIODATA_ASSIGNMENT":
      return " completed a biodata assignment";
    case "VIEW_IMPORT_EXPORT_WORKSPACE":
      return " viewed import and export workspace";
    case "IMPORT_PROFILE_CSV":
      return ` imported a CSV batch${name}`;
    case "VIEW_IMPORT_BATCH":
      return ` viewed an import batch${name}`;
    case "DELETE_IMPORT_BATCH":
      return ` deleted an import batch${name}`;
    case "FINALIZE_IMPORT_BATCH":
      return ` finalized an import batch${name}`;
    case "SEARCH_EXPORT_PROFILES":
      return payload.search_query
        ? ` searched export profiles for "${payload.search_query}"`
        : " searched export profiles";
    case "CREATE_BULK_EXPORT":
      return exportKind
        ? ` created a bulk ${exportKind} export${name}`
        : ` created a bulk export${name}`;
    case "ADD_PAYMENT":
      return ` added a payment${name}`;
    case "ACCESS_DENIED":
      return " was denied access";
    default:
      return ` ${String(log.action || "performed action")
        .toLowerCase()
        .replace(/_/g, " ")}${name}`;
  }
}

function getAuditEntitySummary(log) {
  if (log.action === "RUN_DUPLICATE_CHECK") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const payload = Object.keys(next).length > 0 ? next : previous;
    const payloadName = [
      payload.first_name,
      payload.middle_name,
      payload.last_name,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");
    const profileName = [log.first_name, log.last_name]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");

    return payloadName || profileName || "";
  }

  if (log.entity_type === "profile_document") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const payload = Object.keys(next).length > 0 ? next : previous;
    const documentName =
      (typeof log.document_file_name === "string" && log.document_file_name.trim()) ||
      (typeof payload.document_name === "string" && payload.document_name.trim()) ||
      (typeof payload.original_file_name === "string" && payload.original_file_name.trim()) ||
      (typeof payload.document_type_name === "string" && payload.document_type_name.trim()) ||
      "";
    const profileName = [log.document_profile_first_name, log.document_profile_last_name]
      .filter(Boolean)
      .join(" ");

    return [documentName, profileName].filter(Boolean).join(" | ");
  }

  if (log.entity_type === "profile") {
    return [log.first_name, log.last_name].filter(Boolean).join(" ");
  }

  if (log.entity_type === "profile_document") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const payload = Object.keys(next).length > 0 ? next : previous;
    const documentName =
      (typeof log.document_file_name === "string" && log.document_file_name.trim()) ||
      (typeof payload.document_name === "string" && payload.document_name.trim()) ||
      (typeof payload.original_file_name === "string" && payload.original_file_name.trim()) ||
      (typeof payload.document_type_name === "string" && payload.document_type_name.trim()) ||
      "";
    const profileName = [log.document_profile_first_name, log.document_profile_last_name]
      .filter(Boolean)
      .join(" ");

    return [documentName, profileName].filter(Boolean).join(" Â· ");
  }

  if (log.entity_type === "future_prospect") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const relativeName = typeof source.relative_name === "string" ? source.relative_name.trim() : "";
    const profileId = String(source.profile_id || "").trim();
    return [relativeName, profileId ? `Profile ${profileId}` : ""].filter(Boolean).join(" · ");
  }

  if (log.entity_type === "task") {
    return getTaskSummary(log);
  }

  if (log.entity_type === "profile_assignment") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const parts = [];
    if (typeof source.profile_summary === "string" && source.profile_summary.trim()) {
      parts.push(source.profile_summary.trim());
    } else {
      const fallbackProfile = [
        source.profile_no ? `#${String(source.profile_no).trim()}` : null,
        source.profile_name ? String(source.profile_name).trim() : null,
      ].filter(Boolean).join(" ");
      if (fallbackProfile) parts.push(fallbackProfile);
    }

    const targetSummary = getAssignmentTargetSummary(log);
    if (targetSummary) {
      parts.push(targetSummary);
    }

    return parts.join(" · ");
  }

  if (log.entity_type === "biodata_assignment" || log.entity_type === "biodata_assignment_file") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const parts = [];
    if (typeof source.source_label === "string" && source.source_label.trim()) {
      parts.push(source.source_label.trim());
    }
    const assignmentTarget = getAssignmentTargetSummary(log);
    if (assignmentTarget) {
      parts.push(assignmentTarget);
    }
    if (typeof source.profile_no === "string" && source.profile_no.trim()) {
      parts.push(`Profile #${source.profile_no.trim()}`);
    }
    if (!parts.length && log.entity_id) {
      parts.push(`Item #${log.entity_id}`);
    }
    return parts.join(" · ");
  }

  if (log.entity_type === "import_batch") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const parts = [];
    if (typeof source.original_file_name === "string" && source.original_file_name.trim()) {
      parts.push(source.original_file_name.trim());
    }
    if (source.batch_id || log.entity_id) {
      parts.push(`Batch #${String(source.batch_id || log.entity_id).trim()}`);
    }
    if (typeof source.batch_status === "string" && source.batch_status.trim()) {
      parts.push(source.batch_status.trim().replace(/_/g, " "));
    }
    return parts.join(" Â· ");
  }

  if (log.entity_type === "export_job") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const parts = [];
    if (typeof source.file_name === "string" && source.file_name.trim()) {
      parts.push(source.file_name.trim());
    }
    if (typeof source.profiles_summary === "string" && source.profiles_summary.trim()) {
      parts.push(source.profiles_summary.trim());
    }
    return parts.join(" Â· ");
  }

  if (log.entity_type === "bulk_operation") {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    if (log.action === "SEARCH_EXPORT_PROFILES" && typeof source.search_query === "string" && source.search_query.trim()) {
      return `Search: ${source.search_query.trim()}`;
    }
    if (typeof source.workspace === "string" && source.workspace.trim()) {
      return source.workspace.trim().replace(/_/g, " ");
    }
    return "";
  }

  if (String(log.action || "").startsWith("ACCESS_POLICY_")) {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";

    const parts = [];
    if (typeof source.policy_name === "string" && source.policy_name.trim()) {
      parts.push(source.policy_name.trim());
    }
    const moduleName = String(source.module || "").trim();
    const actionName = String(source.action || "").trim();
    if (moduleName && actionName) {
      parts.push(`${moduleName} / ${actionName}`);
    }
    return parts.join(" · ");
  }

  if (String(log.action || "").startsWith("REFERRAL_SOURCE_")) {
    const next = safeParseObject(log.new_data ?? log.new_value);
    const previous = safeParseObject(log.old_data ?? log.old_value);
    const source = Object.keys(next).length > 0 ? next : previous;
    if (!source || typeof source !== "object") return "";
    return typeof source.name === "string" ? source.name.trim() : "";
  }

  return "";
}

function getAssignmentProfileSummary(log) {
  const payload = safeParseObject(log.new_data ?? log.new_value);
  if (!payload || typeof payload !== "object") return "";

  if (typeof payload.profile_summary === "string" && payload.profile_summary.trim()) {
    return payload.profile_summary.trim();
  }

  if (typeof payload.profiles_summary === "string" && payload.profiles_summary.trim()) {
    const items = payload.profiles_summary
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (items.length <= 2) return items.join(", ");
    return `${items.slice(0, 2).join(", ")} +${items.length - 2} more`;
  }

  const profileNos = splitAuditList(payload.profile_nos);
  const profileNames = splitAuditList(payload.profile_names);
  const count = Math.max(profileNos.length, profileNames.length);
  if (!count) return "";

  const entries = [];
  for (let index = 0; index < count; index += 1) {
    const combined = [profileNos[index] ? `#${profileNos[index]}` : null, profileNames[index]]
      .filter(Boolean)
      .join(" ");
    entries.push(combined || profileNos[index] || profileNames[index]);
  }

  const cleaned = entries.filter(Boolean);
  if (cleaned.length <= 2) return cleaned.join(", ");
  return `${cleaned.slice(0, 2).join(", ")} +${cleaned.length - 2} more`;
}

function getAssignmentTargetSummary(log) {
  const nextPayload = safeParseObject(log.new_data ?? log.new_value);
  const previousPayload = safeParseObject(log.old_data ?? log.old_value);
  const payload = Object.keys(nextPayload).length > 0 ? nextPayload : previousPayload;
  if (!payload || typeof payload !== "object") return "";

  const labels = [
    formatUserAuditLabel(payload.assigned_user_name, payload.assigned_user_email),
    formatUserAuditLabel(payload.assigned_to_name, payload.assigned_to_email),
    formatUserAuditLabel(payload.assigned_by_name, payload.assigned_by_email),
  ].filter(Boolean);

  if (labels.length) {
    return labels[0];
  }

  if (payload.assigned_user_id != null && payload.assigned_user_id !== "") {
    return `user #${payload.assigned_user_id}`;
  }

  if (payload.assigned_to_user_id != null && payload.assigned_to_user_id !== "") {
    return `user #${payload.assigned_to_user_id}`;
  }

  return "";
}

function getTaskSummary(log) {
  const nextPayload = safeParseObject(log.new_data ?? log.new_value);
  const previousPayload = safeParseObject(log.old_data ?? log.old_value);
  const payload = Object.keys(nextPayload).length > 0 ? nextPayload : previousPayload;
  if (!payload || typeof payload !== "object") return "";

  if (typeof payload.task_title === "string" && payload.task_title.trim()) {
    return payload.task_title.trim();
  }

  if (typeof payload.title === "string" && payload.title.trim()) {
    return payload.title.trim();
  }

  return log.entity_id ? `task #${log.entity_id}` : "";
}

function getTaskActorRoleSummary(log) {
  const nextPayload = safeParseObject(log.new_data ?? log.new_value);
  const previousPayload = safeParseObject(log.old_data ?? log.old_value);
  const payload = Object.keys(nextPayload).length > 0 ? nextPayload : previousPayload;
  const role = String(payload?.actor_task_role || "").trim().toLowerCase();

  if (role === "owner" || role === "assigner" || role === "manager") return role;
  return "";
}

function splitAuditList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFieldLabel(field) {
  const labels = {
    first_name: "First Name",
    middle_name: "Middle Name",
    last_name: "Last Name",
    gender: "Gender",
    gender_id: "Gender",
    dob: "Date of Birth",
    birth_date: "Date of Birth",
    birth_time: "Birth Time",
    birth_place: "Birth Place",
    birth_place_id: "Birth Place",
    birth_place_city_id: "Birth Place",
    marital_status: "Marital Status",
    marital_status_id: "Marital Status",
    marital_status_description: "Marital Status Note",
    height: "Height",
    height_cm: "Height",
    height_display: "Height Display",
    complexion: "Complexion",
    complexion_id: "Complexion",
    blood_group: "Blood Group",
    blood_group_id: "Blood Group",
    sampraday: "Sampraday",
    sampraday_id: "Sampraday",
    sampraday_details: "Sampraday Details",
    about_me: "About Me",
    current_location_id: "Current Location",
    city_id: "City",
    state_id: "State",
    district_id: "District",
    tehsil_id: "Tehsil",
    village_id: "Village",
    occupation_type: "Occupation Type",
    company_name: "Company",
    current_occupation_details: "Occupation Details",
    note_type: "Interaction Type",
    outcome: "Result",
    to_profile_name: "Recommended Profile Name",
    follow_up_status: "Follow-up Status",
    follow_up_at: "Next Follow-up",
    follow_up_completed_at: "Follow-up Completed At",
    follow_up_completed_by: "Follow-up Completed By",
    recommendation_id: "Recommendation",
    to_profile_id: "Recommended Profile",
    to_profile_no: "Recommended Profile No",
    previous_status: "Previous Recommendation Status",
    status: "Recommendation Status",
    match_status: "Match Status",
    conversion_from_status: "Converted From Status",
    outcome_source: "Outcome Source",
    contact_side: "For Profile",
    outcome_notes: "Outcome Notes",
    outcome_id: "Outcome",
    duplicate_action: "Duplicate Action",
    duplicate_resolution_mode: "Duplicate Resolution Mode",
    duplicate_matches_count: "Duplicate Matches",
    parent_role_id: "Parent Role",
    assigned_user_id: "Assigned User",
    assigned_user_email: "Assigned User Email",
    assigned_user_name: "Assigned User Name",
    assigned_to_user_id: "Task Owner",
    assigned_to_email: "Task Owner Email",
    assigned_by_user_id: "Task Assigner",
    assigned_by_email: "Task Assigner Email",
    actor_task_role: "Changed By Role",
    task_title: "Task Title",
    profile_id: "Profile",
    profile_ids: "Profiles",
    profile_no: "Profile No",
    profile_nos: "Profile Nos",
    profile_name: "Profile Name",
    profile_names: "Profile Names",
    profile_summary: "Profile",
    profiles_summary: "Profiles",
    recommendation_action_count: "Recommendation Actions",
    admin_notes_count: "Admin Notes Added",
    assignment_type: "Assignment Type",
    is_active: "Active Assignment",
    assigned_at: "Assigned At",
    unassigned_at: "Unassigned At",
    result_bucket: "Result Bucket",
    recommended_by: "Recommended By",
    referral_source_id: "Referral Source",
    referral_source_name: "Referral Source",
    is_verified: "Verified",
    verified_by: "Verified By",
    verified_at: "Verified At",
    document_id: "Document",
    document_name: "Document Name",
    document_type_name: "Document Type",
    original_file_name: "Original File Name",
    mime_type: "MIME Type",
    file_size_bytes: "File Size",
    file_disposition: "Access Mode",
    content_type: "Content Type",
    accessed_at: "Accessed At",
    opened_at: "Opened At",
    downloaded_at: "Downloaded At",
    download_variant: "Download Variant",
    preview_page_count: "Preview Pages",
    policy_name: "Policy Name",
    subject_type: "Subject Type",
    subject_id: "Subject",
    module: "Module",
    action: "Action",
    effect: "Effect",
    priority: "Priority",
    condition_json: "Conditions",
    date_from: "Date From",
    date_to: "Date To",
    deleted_rows: "Deleted Rows",
    selected_duplicate_profile_id: "Selected Duplicate Profile",
    selected_duplicate_case_id: "Selected Duplicate Case",
    primary_profile_id: "Primary Profile",
    primary_profile_no: "Primary Profile No",
    primary_profile_name: "Primary Profile Name",
    duplicate_profile_id: "Duplicate Profile",
    duplicate_profile_no: "Duplicate Profile No",
    duplicate_profile_name: "Duplicate Profile Name",
    canonical_profile_id: "Canonical Profile",
    canonical_profile_no: "Canonical Profile No",
    canonical_profile_name: "Canonical Profile Name",
    merged_profile_id: "Merged Profile",
    merged_profile_no: "Merged Profile No",
    merged_profile_name: "Merged Profile Name",
    profile_merge_history_id: "Merge History",
    work_profile: "Work Profile",
    educations: "Education",
    education: "Education",
    contacts: "Contacts",
    relatives: "Relatives",
    preferences: "Preferences",
    workspace: "Workspace",
    search_query: "Search Query",
    result_count: "Result Count",
    matched_profile_nos: "Matched Profile Nos",
    matched_profile_names: "Matched Profile Names",
    batch_id: "Batch Id",
    batch_status: "Batch Status",
    total_rows: "Total Rows",
    processed_rows: "Processed Rows",
    ready: "Ready Rows",
    duplicate_review: "Duplicate Review Rows",
    invalid: "Invalid Rows",
    finalized_profile_count: "Finalized Profile Count",
    created_profile_count: "Created Profile Count",
    created_profile_nos: "Created Profile Nos",
    created_row_nos: "Created Row Nos",
    export_type: "Export Type",
    section_ids: "Section Ids",
    section_labels: "Sections",
    include_photo: "Include Photo",
    profile_count: "Profile Count",
    section_count: "Section Count",
    row_count: "Row Count",
    import_batches: "Import Batches",
    export_jobs: "Export Jobs",
    pref_min_age: "Preference Min Age",
    pref_max_age: "Preference Max Age",
    pref_preferred_location_id: "Preference Location",
    pref_preferred_marital_status_id: "Preference Marital Status",
    child_tables_updated: "Related Sections",
    upserted_rows: "Upserted Rows",
    tags: "Tags",
  };

  return (
    labels[field] ||
    String(field)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function shorten(value, max = 16) {
  if (!value) return "";
  const str = String(value);
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

function formatIpAddress(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const first = raw
    .split(",")
    .map((item) => item.trim())
    .find((item) => item && item.toLowerCase() !== "unknown");
  if (!first) return "";
  if (first === "::1") return "127.0.0.1";
  if (first.startsWith("::ffff:")) return first.slice(7);
  return first;
}

function formatEntityTypeLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw === "profile_assignment") return "profile assignment";
  if (raw === "profile_document") return "document";
  if (raw === "future_prospect") return "future prospect";
  if (raw === "import_batch") return "import batch";
  if (raw === "export_job") return "export job";
  if (raw === "bulk_operation") return "workspace";
  return raw.replace(/_/g, " ");
}

// ── Styles ─────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: "100%",
    padding: "28px 18px 42px",
  },

  wrap: {
    maxWidth: 1120,
    margin: "0 auto",
    animation: "fadeUp 0.28s ease both",
  },

  hero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 18,
    padding: 24,
    borderRadius: 28,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,247,245,0.94) 100%)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 20px 60px ${BRAND.shadow}`,
    marginBottom: 18,
  },

  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2.8,
    color: BRAND.mix,
    fontWeight: 900,
    marginBottom: 8,
  },

  pageTitle: {
    margin: 0,
    color: BRAND.text,
    fontSize: 32,
    fontWeight: 950,
    letterSpacing: -0.8,
  },

  pageSub: {
    margin: "8px 0 0",
    maxWidth: 680,
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },

  policyNotice: {
    marginBottom: 18,
    padding: "14px 18px",
    borderRadius: 22,
    background: BRAND.redSoft,
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 10px 28px ${BRAND.shadow}`,
  },

  policyNoticeTitle: {
    display: "block",
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 950,
    marginBottom: 6,
  },

  policyNoticeBody: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18,
  },

  statCard: {
    background: "rgba(255,255,255,0.88)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 20,
    padding: 18,
    boxShadow: `0 12px 34px ${BRAND.shadow}`,
  },

  statValue: {
    fontSize: 28,
    fontWeight: 950,
    color: BRAND.red,
  },

  statLabel: {
    marginTop: 3,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.7,
  },

  filtersCard: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    padding: 16,
    borderRadius: 24,
    background: "rgba(255,255,255,0.9)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 14px 38px ${BRAND.shadow}`,
    marginBottom: 18,
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  filterLabel: {
    fontSize: 10,
    color: BRAND.mix,
    fontWeight: 900,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },

  select: {
    height: 42,
    minWidth: 150,
    padding: "0 12px",
    borderRadius: 14,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
  },

  clearBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: "none",
    background: BRAND.redSoft,
    color: BRAND.red,
    fontWeight: 900,
    cursor: "pointer",
  },

  countPill: {
    marginLeft: "auto",
    height: 34,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 12px",
    borderRadius: 999,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.borderStrong}`,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 900,
  },

  loadSummary: {
    margin: "-6px 2px 16px",
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },

  paginationBar: {
    marginTop: 18,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  paginationSummary: {
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.5,
  },

  paginationActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  paginationBtn: {
    minWidth: 42,
    height: 40,
    padding: "0 14px",
    borderRadius: 999,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },

  paginationBtnActive: {
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    color: "#fff",
    borderColor: "transparent",
    boxShadow: `0 12px 24px ${BRAND.shadowStrong}`,
  },

  paginationBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  paginationEllipsis: {
    color: BRAND.mutedSoft,
    fontSize: 14,
    fontWeight: 900,
    padding: "0 2px",
  },

  list: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  item: {
    position: "relative",
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 24,
    padding: "18px 20px 18px 28px",
    boxShadow: `0 14px 38px ${BRAND.shadow}`,
  },

  timelineDot: {
    position: "absolute",
    top: 22,
    left: 12,
    width: 8,
    height: 8,
    borderRadius: "50%",
    boxShadow: "0 0 0 4px rgba(255,255,255,0.9)",
  },

  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },

  itemLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  itemRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  actionBadge: {
    fontSize: 10,
    fontWeight: 950,
    padding: "5px 10px",
    borderRadius: 999,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },

  itemText: {
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.5,
  },

  actor: {
    color: BRAND.text,
    marginRight: 4,
  },

  verb: {
    color: BRAND.muted,
  },

  time: {
    fontSize: 12,
    color: BRAND.mutedSoft,
    fontWeight: 800,
  },

  entityBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
    color: BRAND.muted,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  metaLine: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 8,
    color: BRAND.mutedSoft,
    fontSize: 11,
    fontWeight: 700,
  },

  noteBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 12,
    padding: 12,
    borderRadius: 16,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
  },

  noteIcon: {
    color: BRAND.red,
    fontWeight: 900,
  },

  noteText: {
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.6,
    fontStyle: "italic",
  },

  diffBox: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px dashed ${BRAND.borderStrong}`,
  },

  diffHeading: {
    fontSize: 10,
    color: BRAND.mix,
    textTransform: "uppercase",
    letterSpacing: 1.6,
    fontWeight: 950,
    marginBottom: 10,
  },

  diffTable: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  diffRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  diffField: {
    minWidth: 170,
    color: BRAND.mix,
    fontSize: 12,
    fontWeight: 950,
  },

  diffOld: {
    maxWidth: 260,
    color: BRAND.mutedSoft,
    fontSize: 13,
    textDecoration: "line-through",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  diffArrow: {
    color: BRAND.red,
    fontWeight: 950,
  },

  diffNew: {
    maxWidth: 300,
    color: BRAND.red,
    fontSize: 13,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  complexWrap: {
    marginBottom: 4,
  },

  listChanged: {
    fontSize: 12,
    color: BRAND.red,
    fontStyle: "italic",
  },

  expandBtn: {
    padding: "4px 10px",
    borderRadius: 999,
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.redSoft,
    color: BRAND.red,
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },

  complexGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 10,
  },

  complexPanel: {
    padding: 12,
    borderRadius: 16,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.borderStrong}`,
    overflow: "hidden",
  },

  complexLabelMuted: {
    display: "block",
    marginBottom: 8,
    color: BRAND.mutedSoft,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    fontWeight: 950,
  },

  complexLabelGreen: {
    display: "block",
    marginBottom: 8,
    color: BRAND.red,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    fontWeight: 950,
  },

  pre: {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: BRAND.muted,
    fontSize: 12,
    lineHeight: 1.55,
    fontFamily:
      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
  },

  emptyState: {
    padding: "64px 20px",
    textAlign: "center",
    borderRadius: 28,
    background: "rgba(255,255,255,0.76)",
    border: `1px dashed ${BRAND.borderStrong}`,
  },

  emptyIcon: {
    width: 52,
    height: 52,
    margin: "0 auto 12px",
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    background: BRAND.redSoftAlt,
    color: BRAND.mutedSoft,
    fontSize: 28,
  },

  emptyTitle: {
    margin: 0,
    color: BRAND.text,
    fontSize: 18,
    fontWeight: 950,
  },

  emptyText: {
    margin: "6px 0 0",
    color: BRAND.muted,
    fontSize: 14,
  },

  center: {
    minHeight: "62vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },

  spinner: {
    width: 38,
    height: 38,
    border: `3px solid ${BRAND.borderSoft}`,
    borderTop: `3px solid ${BRAND.red}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  loadingText: {
    color: BRAND.muted,
    fontSize: 14,
    fontWeight: 800,
    animation: "pulse 1.2s ease infinite",
  },

  errorIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: BRAND.redSoft,
    color: BRAND.red,
    fontWeight: 950,
    fontSize: 24,
  },

  errorTitle: {
    margin: "4px 0 0",
    color: BRAND.text,
    fontSize: 20,
    fontWeight: 950,
  },

  errorText: {
    margin: 0,
    color: BRAND.muted,
    fontSize: 14,
  },

  primaryBtn: {
    height: 42,
    padding: "0 18px",
    borderRadius: 999,
    border: "none",
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: `0 14px 30px ${BRAND.shadowStrong}`,
  },
  primaryBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};
